// V4 — Mobile-first shell. Phone-shaped chrome at every viewport:
//   • Compact top app-bar (logo + page title + team switcher + menu)
//   • Single-column scrollable main content
//   • Fixed bottom tab navigation (5 primary destinations)
//   • Slide-up "More" sheet for secondary destinations + theme/sign-out
//
// Logic / data flow mirrors Shell.jsx — same loadState bootstrap,
// same Module dispatch, same setUser plumbing — only the chrome
// changes. Existing views render unmodified; the page-level mobile
// CSS we built in Phase 5 (card-mode tables, etc.) does the rest.

import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Home, CalendarDays, Receipt, Trophy, Menu, X, Search,
  LogOut, Sun, Moon, ChevronRight, Shield, Landmark, ArrowLeftRight, Users
} from "lucide-react";
import { loadState, logout } from "../api";
import { nav, NAV_GROUPS, can } from "../nav";
import { canEnterWorkspace } from "../data";
import Module from "./Module";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import { useWorkspace } from "./Workspace";
import { ActiveTeamProvider } from "./ActiveTeam";
import TeamSwitcher from "./TeamSwitcher";
import { useUnreadMessages } from "./useUnreadMessages";
import { useAutoReload } from "./useAutoReload";
import NotificationBell from "./NotificationBell";
import ChatFab from "./ChatFab";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

// The five tabs pinned at the bottom. All other destinations (including
// League for committee users) are reached via the top-left menu icon
// that opens the "More" sheet. Home is flagged as primary so the middle
// tab reads visually as the anchor point.
const PRIMARY_TABS = [
  { key: "schedule",  label: "Schedule", icon: CalendarDays },
  { key: "members",   label: "Members",  icon: Users },
  { key: "dashboard", label: "Home",     icon: Home, primary: true },
  { key: "squad",     label: "Squad",    icon: Shield },
  { key: "splits",    label: "Split",    icon: Receipt }
];

export default function ShellV4({ user, setUser }) {
  const [state, setState]   = useState(EMPTY_STATE);
  const [active, setActive] = useState("dashboard");
  // Optional payload carried alongside a navigation (see Shell.jsx).
  const [navArg, setNavArg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();
  const { setWorkspace }        = useWorkspace();
  const canGoLeague = canEnterWorkspace(user, "league");
  // Polls every 15s for new direct messages; toasts when count rises.
  const unreadMessages = useUnreadMessages({
    active,
    onNavigate: setActive
  });

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  // Cross-device sync — see useAutoReload for details.
  useAutoReload(reload);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const next = await loadState();
        if (alive) setState(next);
      } catch (err) {
        if (!alive) return;
        if (err.status === 401) { setUser(null); return; }
        setError(err.message || "Failed to load data");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [setUser]);

  function signOut() { logout(); setUser(null); }

  // Filter the global nav by the user's roles, then group by the
  // existing NAV_GROUPS sections so the "More" sheet matches the
  // sidebar layout in V1.
  // can(user, key) — user is the user OBJECT (not just .roles).
  // Each nav item declares its `group`; NAV_GROUPS only carries the
  // section { key, label } pair.
  const accessibleNav = useMemo(
    () => nav.filter(item => !item.desktopOnly && can(user, item.key)),
    [user]
  );
  const groupedNav = useMemo(() => {
    return NAV_GROUPS
      .map(g => ({ ...g, items: accessibleNav.filter(n => n.group === g.key) }))
      .filter(g => g.items.length > 0);
  }, [accessibleNav]);

  const currentLabel = nav.find(n => n.key === active)?.label || "";

  function navigateTo(key, arg = null) {
    setActive(key);
    setNavArg(arg);
    setMoreOpen(false);
  }

  return (
    <ActiveTeamProvider user={user} teams={state.teams}>
      <div className="v4-shell">
        {/* ─── TOP APP BAR ─────────────────────────────────────── */}
        <header className="v4-appbar">
          <div className="v4-appbar-brand">
            <img src="/gabruz-brand.png" alt="Gabruz Universe" onError={e => e.currentTarget.style.display = "none"}/>
            <div>
              <small>GABRUZ</small>
              <strong>{currentLabel}</strong>
            </div>
          </div>
          <NotificationBell notifications={state.notifications} onNavigate={navigateTo} compact/>
          <div className="v4-appbar-actions">
            <TeamSwitcher />
            <button
              type="button"
              className="v4-iconBtn v4-iconBtn--menu"
              onClick={() => setMoreOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20}/>
              {unreadMessages > 0 && (
                <span className="v4-tab-badge v4-iconBtn-badge" aria-label={`${unreadMessages} unread messages`}>
                  {unreadMessages > 99 ? "99+" : unreadMessages}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* ─── SCROLLABLE MAIN ────────────────────────────────── */}
        <main className="v4-main">
          {loading ? (
            <div className="empty"><h2>Loading…</h2><p>Fetching data.</p></div>
          ) : error ? (
            <div className="empty">
              <h2>Couldn't load data</h2>
              <p>{error}</p>
              <button className="primary" onClick={reload}>Retry</button>
            </div>
          ) : (
            <Module
              active={active}
              setActive={navigateTo}
              navArg={navArg}
              state={state}
              reload={reload}
              user={user}
              setUser={setUser}
            />
          )}
        </main>

        {/* ─── BOTTOM TAB BAR ─────────────────────────────────── */}
        <nav className="v4-tabbar" aria-label="Primary">
          {PRIMARY_TABS
            .filter(t => t.key !== "league" || can(user, "league"))
            .map(t => {
              const Icon = t.icon;
              const isActive = active === t.key;
              const cls = [
                "v4-tab",
                isActive && "v4-tab--active",
                t.primary && "v4-tab--primary"
              ].filter(Boolean).join(" ");
              return (
                <button
                  key={t.key}
                  type="button"
                  className={cls}
                  onClick={() => navigateTo(t.key)}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span className="v4-tab-iconWrap">
                    <Icon size={t.primary ? 26 : 20}/>
                  </span>
                  <span>{t.label}</span>
                </button>
              );
            })}
        </nav>

        <ChatFab active={active} unread={unreadMessages} onOpen={() => setActive("messages")} />

        {/* ─── MORE SHEET (bottom drawer for secondary nav) ───── */}
        {moreOpen && (
          <>
            <div className="v4-sheetBackdrop" onClick={() => setMoreOpen(false)}/>
            <aside className="v4-sheet" aria-label="More navigation">
              <div className="v4-sheet-handle" aria-hidden="true"/>
              <div className="v4-sheet-head">
                <div className="v4-sheet-user">
                  <div className="avatar">{user.avatar}</div>
                  <div>
                    <strong>{user.name}</strong>
                    <small>
                      <Shield size={11}/>
                      {(user.roles || [])[0]?.replace("_", " ")}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="v4-iconBtn"
                  onClick={() => setMoreOpen(false)}
                  aria-label="Close menu"
                ><X size={20}/></button>
              </div>

              {/* All nav groups, scrolled if needed */}
              <div className="v4-sheet-body">
                {groupedNav.map(group => (
                  <div key={group.key} className="v4-sheet-group">
                    <h4>{group.label}</h4>
                    <ul>
                      {group.items.map(item => {
                        const Icon = item.icon;
                        const itemBadge = item.key === "messages" && unreadMessages > 0
                          ? unreadMessages : null;
                        return (
                          <li key={item.key}>
                            <button
                              type="button"
                              className={item.key === active ? "v4-sheet-link v4-sheet-link--active" : "v4-sheet-link"}
                              onClick={() => navigateTo(item.key)}
                            >
                              <Icon size={16}/>
                              <span>{item.label}</span>
                              {itemBadge && (
                                <span className="v4-sheet-link-badge">
                                  {itemBadge > 99 ? "99+" : itemBadge}
                                </span>
                              )}
                              <ChevronRight size={14} className="v4-sheet-link-chev"/>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}

                {/* Settings — theme + version + sign out */}
                <div className="v4-sheet-group">
                  <h4>Settings</h4>
                  <ul>
                    {canGoLeague && (
                      <li>
                        <button type="button" className="v4-sheet-link"
                          onClick={() => setWorkspace("league")}>
                          <Landmark size={16}/>
                          <span>Switch to League workspace</span>
                        </button>
                      </li>
                    )}
                    <li>
                      <button type="button" className="v4-sheet-link" onClick={toggleTheme}>
                        {theme === "light" ? <Moon size={16}/> : <Sun size={16}/>}
                        <span>Switch to {theme === "light" ? "dark" : "light"} theme</span>
                      </button>
                    </li>
                    <li className="v4-sheet-versionRow">
                      <span>Design version</span>
                      <div className="v4-versionGroup" role="radiogroup" aria-label="Design version">
                        {[{v:"v3",label:"Desktop"},{v:"v4",label:"Mobile"}].map(({v,label}) => (
                          <button
                            key={v}
                            type="button"
                            className={version === v ? "v4-versionBtn v4-versionBtn--active" : "v4-versionBtn"}
                            onClick={() => setVersion(v)}
                            role="radio"
                            aria-checked={version === v}
                          >{label}</button>
                        ))}
                      </div>
                    </li>
                    <li>
                      <button type="button" className="v4-sheet-link v4-sheet-link--danger" onClick={signOut}>
                        <LogOut size={16}/>
                        <span>Sign out</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>
          </>
        )}
      </div>
    </ActiveTeamProvider>
  );
}
